import React, { useState, useEffect } from 'react';
import { Spin } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { getSubtitle } from './utils';
import Toolbar from '../../components/common/Toolbar/Toolbar';
import HeadingBlock from '../../components/ui/HeadingBlock/HeadingBlock';
import HousesTable from '../../components/ui/tables/HousesTable/HousesTable';
import useHousingStockState from '../../hooks/useHousingStockState';
import useHousingStockHousesState from '../../hooks/useHousingStockHousesState';
import { housesViews } from '../../constants/housesViews';
import HousesWithCameraTable from '../../components/ui/tables/HousesWithCameraTable/HousesWithCameraTable';
import styles from './HousesPage.module.scss';
import { setSearchFieldValue } from '../../store/actions/housingStockActions';
import { modes } from '../../components/ui/MainHeader/components/Search';

const getSelector = (key) => (state) => state.housingStockStore[key];

const HousesPage = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const { pathname } = useLocation();

  const currentCompany = useSelector(getSelector('currentCompany'));
  const housesView = useSelector(getSelector('housesView'));

  const [aliveClientsMap, setAliveClientsMap] = useState(new Map());
  const [data, setData] = useState([]);
  const [subtitle, setSubtitle] = useState('');

  const {
    housingStockHousesState: { data: houses, isFetching: isHousesFetching },
  } = useHousingStockHousesState();

  const {
    housingStockState: {
      data: housingStock,
      isFetching: isHousingStockFetching,
    },
  } = useHousingStockState();

  const handleRowClick = (houseId) => {
    history.push(`${pathname}/${houseId}`);
  };

  // count how many tenants in each house have a mobile app
  useEffect(() => {
    if (Boolean(housingStock?.length)) {
      const newAliveClients = new Map();

      housingStock.forEach((house) => {
        const alive = house.clients.filter((client) => client?.alive === true);

        let aliveClientCount = newAliveClients.get(house.houseId);

        if (aliveClientCount === undefined) {
          newAliveClients.set(house.houseId, alive.length);
        } else {
          newAliveClients.set(house.houseId, aliveClientCount + alive.length);
        }
      });

      setAliveClientsMap(newAliveClients);
    }
  }, [housingStock]);

  // set data
  useEffect(() => {
    let newData = [];

    if (houses?.length) {
      newData = houses.slice().map((house) => {
        house.aliveClientCount = aliveClientsMap.get(house.houseId) ?? 0;
        return house;
      });
    }

    setData(newData);
  }, [houses, aliveClientsMap]);

  //set subtitle
  useEffect(() => {
    let newSubtitle = '';

    if (data.length > 0) {
      newSubtitle = getSubtitle(data);
    }

    setSubtitle(newSubtitle);
  }, [data]);

  useEffect(() => {
    const resetData = Object.values(modes).reduce((acc, value) => {
      if (value !== modes.houses) {
        acc[value] = '';
      }
      return acc;
    }, {});
    dispatch(setSearchFieldValue(resetData));
  }, []);

  return (
    <Spin spinning={isHousesFetching || isHousingStockFetching}>
      <article className={styles.container}>
        <Toolbar
          className={styles.toolbar}
          background="dark"
        >
          <HeadingBlock
            title={currentCompany?.name}
            subtitle={subtitle}
            name={'house'}
            currentCompany={currentCompany}
          />
        </Toolbar>
        {housesView === housesViews.list && (
          <HousesTable
            houses={data}
            onRowClick={handleRowClick}
            rowKey="houseId"
          />
        )}
        {housesView === housesViews.cameras && <HousesWithCameraTable />}
      </article>
    </Spin>
  );
};

export default HousesPage;
